const feature = {
  slug: "instagram-order-management",
  path: "/features/instagram-order-management",
  eyebrow: "Instagram order management",
  h1: "Instagram order management without the spreadsheet",
  seoTitle: "Instagram Order Management for Sellers | Checkout DM",
  description:
    "Every Instagram DM order lands in one dashboard with payment status, shipping, returns and customer history, so you stop hunting through chats.",
  intro:
    "Most Instagram sellers start by keeping orders in a notes app, a spreadsheet or the DM scroll. It works for ten orders. By the fiftieth you are scrolling to find who paid and who is waiting for a parcel. Checkout DM replaces that with one list.",
  glance: [
    { label: "Orders tab", value: "On every plan" },
    { label: "Shipping controls", value: "Starter, Growth and Pro" },
    { label: "Webhooks and team seats", value: "Pro" },
  ],
  blocks: [
    { type: "h2", text: "One row per order" },
    {
      type: "p",
      text:
        "Every order placed through the bot appears in the Orders tab. Each row shows what you need at a glance:",
    },
    {
      type: "ul",
      items: [
        "**Order number**, such as CDM-000140, which you can use when a customer quotes it to you.",
        "**Customer**, linked to their Instagram profile when their handle is on file.",
        "**Items**, one per line, with the size and quantity.",
        "**Total**, with a View breakup link that shows the subtotal, discount and shipping separately.",
        "**Status**, such as pending payment, paid or returned, shown as a coloured badge you can tap for a plain explanation.",
        "**Date** the order was placed.",
      ],
    },
    {
      type: "p",
      text:
        "On Growth and Pro, a further column shows which discount applied. Our guide to [Checkout DM status badges](/blog/checkout-dm-status-badges-explained) lists every status.",
    },

    { type: "h2", text: "Confirming payments" },
    {
      type: "p",
      text:
        "With a payment gateway, payments are confirmed automatically and the order is marked paid. With manual UPI, QR code or bank transfer, the customer sends a screenshot in the DM, it appears in your Orders tab, and you check it against your bank account before pressing Confirm or Reject. The screenshot stays visible in your Instagram thread, and Checkout DM deletes its own copy after 90 days. See [how to avoid fake payment screenshots](/blog/avoid-fake-payment-screenshots-instagram-sellers).",
    },

    { type: "h2", text: "Shipping and the return window" },
    {
      type: "p",
      text:
        "On Starter, Growth and Pro, each order shows a Mark as Shipped button. Pressing it records the ship date and starts the return window, plus a short buffer for transit. When a courier partner is connected through Checkout DM, the tracking link is sent to the buyer automatically by email and, for physical products, in the DM, so the buyer can follow the parcel without asking you. You can use the same link yourself. When your courier is connected, delivery status updates to Delivered, Returned to origin or Failed instead of staying at Shipped. Free accounts see items and payment status but not the shipping controls. Our guide to [packing and shipping Instagram orders](/blog/pack-and-ship-instagram-orders) covers the physical side.",
    },

    { type: "h2", text: "Returns and exchanges, tracked" },
    {
      type: "p",
      text:
        "A customer can request a return or exchange in the DM. Checkout DM updates stock when a return is processed, checks that an exchange size is in stock, and can schedule a reverse pickup if you have connected a courier. Courier pickup of the old item and shipping the replacement still need your action. Returns appear in their own tab, so they never hide inside a chat.",
    },
    {
      type: "chat",
      shop: "yourshop",
      title: "Return request",
      caption: "Illustrative example. Your own messages use your products and wording.",
      messages: [
        { from: "buyer", text: "Hi, I want to exchange my kurta set for size L." },
        { from: "shop", text: "Sure. Which order is this for?" },
        { from: "buyer", text: "CDM-000140" },
        { from: "shop", text: "Thanks. We are checking that size L is in stock." },
      ],
    },

    { type: "h2", text: "Know which chats need you" },
    {
      type: "p",
      text:
        "On Starter, Growth and Pro, the Stuck tab lists conversations that need a person: the bot could not understand a reply, a cart has been sitting untouched, a chosen product was deleted, there has been no reply for 7 days or more, or the customer asked for a human. If the tab is empty, nothing is waiting on you.",
    },

    { type: "h2", text: "As you grow" },
    {
      type: "ul",
      items: [
        "**Customer history.** The full purchase history of each customer. The Free plan has a limited view, and the Creator plan shows order history only.",
        "**Analytics on Growth and Pro.** Revenue and conversion reports, with deeper reporting on Pro. Read [what every number in Checkout DM analytics means](/blog/understand-checkout-dm-analytics-numbers).",
        "**CSV export on Growth.** Download customers, orders and returns for your own records.",
        "**Webhooks and team members on Pro.** Push orders and returns to your own system in real time, and add team members with their own access.",
      ],
    },

    { type: "h2", text: "What it does not do" },
    {
      type: "ul",
      items: [
        "**It does not ship your parcels.** You pack and hand over the order, and courier connections only update the status.",
        "**It is not an accounting tool.** It records orders, discounts and totals but does not replace your bookkeeping.",
        "**It only records orders placed through Checkout DM.** Orders taken outside the bot do not appear.",
      ],
    },
  ],
  related: [
    { to: "/features/instagram-product-catalog-checkout", title: "Catalog and checkout", description: "How orders are created inside the chat." },
    { to: "/features/instagram-dm-automation", title: "Instagram DM automation", description: "The conversation behind every order." },
    { to: "/pricing", title: "Checkout DM pricing", description: "Which plan includes shipping, analytics and team seats." },
    { to: "/faq", title: "Frequently asked questions", description: "Answers on payments, shipping and returns." },
  ],
  faqs: [
    {
      q: "How do I manage Instagram orders without a spreadsheet?",
      a: "Use an order dashboard where every order from the chat appears with its items, total, payment status and date. In Checkout DM, that is the Orders tab, available on every plan.",
    },
    {
      q: "How do I confirm a UPI payment?",
      a: "The customer sends a payment screenshot in the DM, it appears in your Orders tab, and you check it against your bank account before pressing Confirm or Reject. Gateway payments are confirmed automatically.",
    },
    {
      q: "Can buyers track their orders?",
      a: "Yes. When a courier partner is connected through Checkout DM, the tracking link is sent to the buyer automatically by email and, for physical products, in the DM, so they can follow the parcel themselves. You can use the same link. On Starter, Growth and Pro, your Orders tab also shows Delivered, Returned to origin or Failed automatically.",
    },
    {
      q: "How are returns handled?",
      a: "A customer requests a return or exchange in the DM. Checkout DM updates stock when a return is processed and checks that the exchange size is in stock. You still handle courier pickup and shipping the replacement.",
    },
    {
      q: "Can my team use the dashboard?",
      a: "Team members are available on Pro, which includes one admin and two users, with extra seats available.",
    },
  ],
};

export default feature;
